"use client";
import React, { useState, useRef, useEffect } from 'react';

interface PrivacyCompliantMapProps {
  mapsUrl: string;
}

const PrivacyCompliantMap: React.FC<PrivacyCompliantMapProps> = ({ mapsUrl }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userConsent, setUserConsent] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeKey, setIframeKey] = useState(0);

  const handleLoadMap = () => {
    setUserConsent(true);
    setMapLoaded(true);
    // Neue iframe-Instanz mit neuem Key erstellen
    setIframeKey(prev => prev + 1);
  };

  const clearGoogleData = () => {
    try {
      // Wichtig: Iframe komplett aus DOM entfernen, damit keine weitere Kommunikation stattfindet
      if (iframeRef.current && iframeRef.current.parentNode) {
        // Iframe src auf about:blank setzen, um alle Verbindungen zu trennen
        iframeRef.current.src = 'about:blank';
        // Kurz warten, dann komplett entfernen
        setTimeout(() => {
          if (iframeRef.current && iframeRef.current.parentNode) {
            iframeRef.current.parentNode.removeChild(iframeRef.current);
          }
        }, 100);
      }

      // Alle Cookies im aktuellen Kontext löschen
      const cookies = document.cookie.split(";");
      cookies.forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
        
        if (name) {
          // Versuche, Cookies mit verschiedenen Pfaden und Domains zu löschen
          const paths = ['/', '/contact', window.location.pathname];
          const domains = [
            window.location.hostname,
            `.${window.location.hostname}`,
            window.location.hostname.split('.').slice(-2).join('.'),
            `.${window.location.hostname.split('.').slice(-2).join('.')}`
          ];
          
          paths.forEach(path => {
            domains.forEach(domain => {
              // Verschiedene Cookie-Lösch-Varianten versuchen
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};SameSite=None;Secure`;
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};SameSite=Lax`;
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};SameSite=Strict`;
            });
          });
        }
      });

      // SessionStorage komplett löschen
      try {
        sessionStorage.clear();
      } catch (e) {
        // Fallback: Einzelne Keys löschen
        try {
          Object.keys(sessionStorage).forEach(key => {
            sessionStorage.removeItem(key);
          });
        } catch (e2) {
          console.warn('Could not clear sessionStorage:', e2);
        }
      }

      // LocalStorage komplett löschen
      try {
        localStorage.clear();
      } catch (e) {
        // Fallback: Einzelne Keys löschen
        try {
          Object.keys(localStorage).forEach(key => {
            localStorage.removeItem(key);
          });
        } catch (e2) {
          console.warn('Could not clear localStorage:', e2);
        }
      }

      // IndexedDB löschen
      if ('indexedDB' in window && indexedDB.databases) {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            if (db.name) {
              // The IDBDeleteDatabaseRequest returned by deleteDatabase doesn't have .catch, need event handlers
              const request = indexedDB.deleteDatabase(db.name);
              request.onerror = (event) => {
                // @ts-ignore - e is event
                console.warn(`Could not delete IndexedDB ${db.name}:`, event);
              };
            }
          });
        }).catch(e => {
          console.warn('Could not access IndexedDB:', e);
        });
      }

      // Service Worker Cache löschen (falls vorhanden)
      if ('serviceWorker' in navigator && 'caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            if (cacheName.toLowerCase().includes('google') || 
                cacheName.toLowerCase().includes('maps')) {
              caches.delete(cacheName);
            }
          });
        }).catch(e => {
          console.warn('Could not clear caches:', e);
        });
      }
    } catch (error) {
      console.warn('Error clearing Google data:', error);
    }
  };

  const handleRevokeConsent = () => {
    // Iframe entfernen
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank';
    }
    
    // Google-Daten löschen
    clearGoogleData();
    
    // State zurücksetzen
    setMapLoaded(false);
    setUserConsent(false);
    
    // Neue iframe-Instanz für nächstes Laden vorbereiten
    setIframeKey(prev => prev + 1);
  };

  if (!mapsUrl) {
    return (
      <div className="w-full h-64 md:h-full bg-gray-200 rounded-2xl flex items-center justify-center">
        <p className="text-gray-500">Karte nicht verfügbar</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-64 md:h-full rounded-2xl overflow-hidden shadow-lg bg-gray-100">
      {!mapLoaded ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200 p-8">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-company-color-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Karte anzeigen</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Durch Klicken auf den Button werden Inhalte von externen Anbietern geladen. 
              Dabei können personenbezogene Daten an Dritte übermittelt werden.
            </p>
            <button
              onClick={handleLoadMap}
              className="px-6 py-3 bg-company-color-primary text-white font-semibold rounded-lg hover:bg-opacity-90 transition-colors duration-200"
            >
              Karte laden
            </button>
          </div>
        </div>
      ) : (
        <div className="relative w-full h-full">
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={mapsUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Standort Karte"
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
          <button
            onClick={handleRevokeConsent}
            className="absolute top-4 right-4 px-4 py-2 bg-white text-gray-700 font-semibold rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 z-10 flex items-center gap-2"
            title="Karte ausblenden und alle Daten löschen"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Karte ausblenden
          </button>
        </div>
      )}
    </div>
  );
};

export default PrivacyCompliantMap;

